/**
 * Sheet — accessible bottom-sheet (mobile) / side-sheet (desktop) primitive.
 *
 * Uses framer-motion for animation, traps focus with native HTMLElement.focus,
 * closes on Esc and outside-click. No extra deps.
 *
 *   <Sheet open={open} onClose={close} title="Filter">
 *     …content…
 *   </Sheet>
 *
 * Mobile  : slides up from the bottom, rounded top corners, drag-to-dismiss.
 * Desktop : slides in from the right.
 */
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { css } from './tokens';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Right-rail width on desktop. Defaults to 420px. */
  width?: number | string;
  /** Bottom-sheet max height on mobile. Defaults to 88vh. */
  maxHeight?: number | string;
  /** Placement: bottom-on-mobile/right-on-desktop is the default. */
  placement?: 'auto' | 'bottom' | 'right';
  /** Hide the default top-right close button. */
  hideClose?: boolean;
  /** Custom footer (sticky at the bottom on mobile). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export const Sheet: React.FC<SheetProps> = ({
  open,
  onClose,
  title,
  width = 420,
  maxHeight = '88vh',
  placement = 'auto',
  hideClose,
  footer,
  children,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Esc to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the sheet for a11y.
    setTimeout(() => sheetRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            key="sheet-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.55)',
              zIndex: 1049,
            }}
            aria-hidden
          />
          {/* Sheet */}
          <motion.div
            ref={sheetRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
            key="sheet-panel"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            drag={placement !== 'right' ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 500) onClose();
            }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              background: css.color.surface,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              boxShadow: '0 -20px 40px rgba(0,0,0,0.25)',
              maxHeight,
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
            }}
            className={`pm-sheet pm-sheet--${placement}`}
          >
            {/* Drag handle (mobile cue) */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
              <span
                aria-hidden
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 999,
                  background: css.color.surface3,
                }}
              />
            </div>

            {/* Header */}
            {(title || !hideClose) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px 8px',
                  borderBottom: title ? `1px solid ${css.color.border}` : 'none',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 17, color: css.color.text }}>
                  {title}
                </div>
                {!hideClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                      background: css.color.surface2,
                      border: 'none',
                      borderRadius: 999,
                      width: 32,
                      height: 32,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: css.color.text,
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div
                style={{
                  borderTop: `1px solid ${css.color.border}`,
                  padding: 12,
                  background: css.color.surface,
                  paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                }}
              >
                {footer}
              </div>
            )}
          </motion.div>

          <style>{`
            /* Desktop : swap from bottom-sheet to right-rail when placement is
               auto and viewport is wide enough. */
            @media (min-width: 768px) {
              .pm-sheet.pm-sheet--auto,
              .pm-sheet.pm-sheet--right {
                top: 0 !important;
                bottom: 0 !important;
                left: auto !important;
                right: 0 !important;
                max-height: 100vh !important;
                width: min(${typeof width === 'number' ? `${width}px` : width}, 92vw);
                border-top-left-radius: 16px;
                border-top-right-radius: 0;
                border-bottom-left-radius: 16px;
              }
            }
          `}</style>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sheet;

